import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	createOrderDiscountRequest,
	getOrderDetailsCombinedRequest,
} from 'redux/reducers/orders/reducer';
import { getConfirmedOrders, getOrderDetailsCombined } from 'redux/reducers/orders/selectors';
import { DialogContent, Divider, TextField, Typography } from '@mui/material';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { TCreateDiscountDialogProps } from './types';
import { CreateDiscountItem } from './CreateDiscountItem';

export const CreateDiscountDialog: FC<TCreateDiscountDialogProps> = ({
	open,
	setClose,
	orderId,
}) => {
	const dispatch = useDispatch();
	const orderDetailsCombined = useSelector(getOrderDetailsCombined);
	const createDiscountItems = orderDetailsCombined?.orderDiscountItems;
	const orderDiscounts = orderDetailsCombined?.orderDiscounts;
	const orderReturns = orderDetailsCombined?.orderReturns;
	const orders = useSelector(getConfirmedOrders);

	const [selectedCurrenciesArray, setSelectedCurrenciesArray] = useState<string[]>(['%']);

	const [amountPercentage, setAmountPercentage] = useState<string[]>([]);

	const [createDiscountItemsFiltered, setCreateDiscountItemsFiltered] =
		useState(createDiscountItems);

	const handleClose = () => {
		setClose();
		setCreateDiscountItemsFiltered(createDiscountItems);
	};

	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
	const handleConfirmationDialogOpen = () => {
		setConfirmationDialogOpen(true);
	};
	const handleConfirmationDialogClose = () => {
		setConfirmationDialogOpen(false);
	};

	const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setCreateDiscountItemsFiltered(
			createDiscountItems?.filter(
				(item) =>
					item.shortDescription.toLowerCase().includes(event.target.value.toLowerCase()) ||
					item.idProvidedByEm.toLowerCase().includes(event.target.value.toLowerCase()),
			),
		);
	};

	useEffect(() => {
		setSelectedCurrenciesArray(createDiscountItems?.map(() => '%') ?? ['%']);
		setAmountPercentage(createDiscountItems?.map(() => '0') ?? ['0']);
		setCreateDiscountItemsFiltered(createDiscountItems);
	}, [createDiscountItems]);

	useEffect(() => {
		if (!open || !orderId) return;
		dispatch(
			getOrderDetailsCombinedRequest({
				orderId,
			}),
		);
	}, [open, dispatch, orderId]);

	const handleCreateDiscount = () => {
		if (!orderId || !createDiscountItems) return;
		const discountItemsArray =
			createDiscountItems?.map((item, index) => ({
				name: 0,
				key: 0,
				isListField: true,
				fieldKey: 0,
				id: item.id,
				emId: item.emId,
				timestampYear: item.timestampYear,
				externalOrderId: item.externalOrderId,
				orderStatus: item.orderStatus,
				shortDescription: item.shortDescription,
				idProvidedByEm: item.idProvidedByEm,
				quantity: item.quantity,
				costProvidedByEm: item.costProvidedByEm,
				vatRate: item.vatRate,
				vatValue: item.vatValue,
				scheme: item.scheme,
				itemCostIncludingVat: item.itemCostIncludingVat,
				previousDiscounts: item.previousDiscounts,
				previousReturns: item.previousReturns,
				inputDisabled: false,
				amount:
					(selectedCurrenciesArray[index] === '%' ? undefined : +amountPercentage[index]) ??
					undefined,
				percentage:
					(selectedCurrenciesArray[index] === '%' ? +amountPercentage[index] : undefined) ??
					undefined,
			})) ?? [];

		const items = discountItemsArray.filter(
			(item) => (item?.amount ?? 0) > 0 || (item?.percentage ?? 0) > 0,
		);

		dispatch(
			createOrderDiscountRequest({
				confirmed: true,
				id: items[0].id,
				orderId,
				discountBreakdown: items,
				discountDate: new Date().toISOString(),
				timestampYear: items[0].timestampYear,
			}),
		);
		handleClose();
		handleConfirmationDialogClose();
	};

	const handleSelectedCurrency = (currency: string, index: number) => {
		const newArray = [...selectedCurrenciesArray];
		newArray[index] = currency;
		setSelectedCurrenciesArray(newArray);
		const newAmountPercentage = [...amountPercentage];

		if (currency === '%')
			newAmountPercentage[index] = (
				(+newAmountPercentage[index] * 100) /
				(createDiscountItems?.[index].itemCostIncludingVat ?? 1)
			).toFixed(2);
		else
			newAmountPercentage[index] = (
				(+newAmountPercentage[index] * (createDiscountItems?.[index].itemCostIncludingVat ?? 0)) /
				100
			).toFixed(2);

		setAmountPercentage(newAmountPercentage);
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={handleClose}
				sx={{
					'& .MuiDialog-paper': {
						borderRadius: '12px',
						minWidth: '500px',
						maxWidth: '95%',
					},
				}}
			>
				<DialogTitle color="primary">Creating Order Discount</DialogTitle>
				<DialogContent>
					{(createDiscountItems?.length ?? 0) > 5 && (
						<TextField
							label="Search"
							variant="outlined"
							fullWidth
							size="small"
							sx={{ mb: 3, mt: 1, '& .MuiInputBase-root': { borderRadius: '12px' } }}
							onChange={handleSearch}
						/>
					)}
					{createDiscountItemsFiltered?.map((item, index) => (
						<CreateDiscountItem
							key={`${item.id}-${index}`}
							createDiscountItem={item}
							returnItemQuantity={orderReturns
								?.flatMap((r) => r?.orderReturnItems ?? [])
								.filter((element) => element?.idProvidedByEm === item.idProvidedByEm)
								.reduce((acc, curr) => acc + curr.returnItemQuantity, 0)}
							discount={
								orderDiscounts
									?.find((child) =>
										child.orderDiscountItems.some(
											(element) => element.idProvidedByEm === item.idProvidedByEm,
										),
									)
									?.orderDiscountItems.find(
										(element) => element.idProvidedByEm === item.idProvidedByEm,
									)?.discountPercentage
							}
							// value={discountItemsArray[index].amount ?? 0}
							value={amountPercentage[index] ?? '0'}
							setValue={(value) => {
								// setDiscountItemsArray(() => {
								// 	const newState = [...discountItemsArray];
								// 	newState[index].quantity = value;
								// 	return newState;
								// });
								setAmountPercentage(() => {
									const newState = [...amountPercentage];
									newState[index] = value;
									return newState;
								});
							}}
							paymentCurrency={orders?.data?.[0]?.paymentCurrency ?? ''}
							setSelectedCurrency={(currency) => handleSelectedCurrency(currency, index)}
							selectedCurrency={selectedCurrenciesArray[index]}
						/>
					))}
					<Divider sx={{ mt: 2, mb: 1, maxWidth: '280px' }} />
					<Typography variant="body2" color="text.secondary">
						All Amounts are indicated including VAT
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>Cancel</Button>
					<Button
						onClick={handleConfirmationDialogOpen}
						autoFocus
						disabled={!amountPercentage.some((item) => +item > 0)}
					>
						Create Order Discount
					</Button>
				</DialogActions>
			</Dialog>
			<ConfirmationDialog
				open={confirmationDialogOpen}
				setClose={handleConfirmationDialogClose}
				handleConfirm={handleCreateDiscount}
				title="Order discount can`t be changed or deleted.<br />Please confirm saving of the Order discount."
			/>
		</>
	);
};
